import React, { useState, useEffect } from 'react';
import {
  Megaphone,
  Plus,
  Search,
  Edit2,
  Trash2,
  Play,
  Pause,
  CheckCircle,
  Archive,
  AlertCircle,
  X,
  BarChart3,
  Calendar,
  Clock,
  Phone,
  Users,
} from 'lucide-react';
import { campaignApi, CampaignDTO, CampaignStatus, CampaignType } from '../../services/ivrApi';
import OrganizationFilter from '../../components/Filters/OrganizationFilter';
import { useScopedOrgId } from '../../hooks/useScopedOrgId';

// Status badge colors
const statusColors: Record<CampaignStatus, { bg: string; text: string }> = {
  DRAFT: { bg: 'bg-gray-100', text: 'text-gray-800' },
  ACTIVE: { bg: 'bg-green-100', text: 'text-green-800' },
  PAUSED: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  COMPLETED: { bg: 'bg-blue-100', text: 'text-blue-800' },
  ARCHIVED: { bg: 'bg-purple-100', text: 'text-purple-800' },
};

const typeLabels: Record<CampaignType, string> = {
  OUTBOUND: 'Outbound',
  INBOUND: 'Inbound',
  HYBRID: 'Hybrid',
};

const CampaignManagement: React.FC = () => {
  const [campaigns, setCampaigns] = useState<CampaignDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<CampaignStatus | ''>('');
  const [showModal, setShowModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<CampaignDTO | null>(null);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [selectedCampaignStats, setSelectedCampaignStats] = useState<CampaignDTO | null>(null);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  // Business admins get their own organization implicitly; provider-level roles
  // choose one. See useScopedOrgId — before it, every role outside the chooser
  // list was left with no organization at all and a dead page.
  const { selectedOrgId, setSelectedOrgId, showOrgFilter } = useScopedOrgId();

  useEffect(() => {
    if (selectedOrgId) {
      loadCampaigns();
    } else {
      setCampaigns([]);
    }
  }, [selectedOrgId]);

  const loadCampaigns = async () => {
    if (!selectedOrgId) return;

    setLoading(true);
    try {
      const response = await campaignApi.getAll(selectedOrgId);
      if (response.success && response.data) {
        setCampaigns(response.data);
      } else {
        showNotification('error', response.error || 'Failed to load campaigns');
      }
    } catch (error) {
      showNotification('error', 'Network error loading campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCampaign = async (campaign: Partial<CampaignDTO>) => {
    try {
      const campaignData = {
        campaignName: campaign.campaignName || '',
        description: campaign.description,
        campaignType: campaign.campaignType || 'OUTBOUND',
        startDate: campaign.startDate,
        endDate: campaign.endDate,
        maxConcurrentCalls: campaign.maxConcurrentCalls,
        callsPerMinute: campaign.callsPerMinute,
        overrideBusinessHours: campaign.overrideBusinessHours,
        allowDuplicateRecipients: campaign.allowDuplicateRecipients,
      };

      const response = editingCampaign
        ? await campaignApi.update(selectedOrgId, editingCampaign.id!, campaignData)
        : await campaignApi.create(selectedOrgId, campaignData as any);

      if (response.success) {
        await loadCampaigns();
        setShowModal(false);
        setEditingCampaign(null);
        showNotification(
          'success',
          editingCampaign ? 'Campaign updated successfully' : 'Campaign created successfully'
        );
      } else {
        showNotification('error', response.error || 'Failed to save campaign');
      }
    } catch (error) {
      showNotification('error', 'Network error saving campaign');
    }
  };

  const handleStatusAction = async (campaign: CampaignDTO, action: 'activate' | 'pause' | 'resume' | 'complete' | 'archive') => {
    try {
      let response;
      switch (action) {
        case 'activate':
          response = await campaignApi.activate(selectedOrgId, campaign.id!);
          break;
        case 'pause':
          response = await campaignApi.pause(selectedOrgId, campaign.id!);
          break;
        case 'resume':
          response = await campaignApi.resume(selectedOrgId, campaign.id!);
          break;
        case 'complete':
          response = await campaignApi.complete(selectedOrgId, campaign.id!);
          break;
        case 'archive':
          response = await campaignApi.archive(selectedOrgId, campaign.id!);
          break;
      }

      if (response.success) {
        await loadCampaigns();
        showNotification('success', `Campaign ${action}d successfully`);
      } else {
        showNotification('error', response.error || `Failed to ${action} campaign`);
      }
    } catch (error) {
      showNotification('error', `Network error ${action}ing campaign`);
    }
  };

  const handleDeleteCampaign = async (id: number, name: string) => {
    if (confirm(`Delete campaign "${name}"? This action cannot be undone.`)) {
      try {
        const response = await campaignApi.delete(selectedOrgId, id);
        if (response.success) {
          await loadCampaigns();
          showNotification('success', 'Campaign deleted successfully');
        } else {
          showNotification('error', response.error || 'Failed to delete campaign');
        }
      } catch (error) {
        showNotification('error', 'Network error deleting campaign');
      }
    }
  };

  const handleViewStats = (campaign: CampaignDTO) => {
    setSelectedCampaignStats(campaign);
    setShowStatsModal(true);
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesSearch =
      campaign.campaignName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || campaign.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getActionButtons = (campaign: CampaignDTO) => {
    const buttons = [];

    switch (campaign.status) {
      case 'DRAFT':
        buttons.push(
          <button
            key="activate"
            onClick={() => handleStatusAction(campaign, 'activate')}
            className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50"
            title="Activate"
          >
            <Play className="w-4 h-4" />
          </button>
        );
        break;
      case 'ACTIVE':
        buttons.push(
          <button
            key="pause"
            onClick={() => handleStatusAction(campaign, 'pause')}
            className="text-yellow-600 hover:text-yellow-800 p-1 rounded hover:bg-yellow-50"
            title="Pause"
          >
            <Pause className="w-4 h-4" />
          </button>
        );
        buttons.push(
          <button
            key="complete"
            onClick={() => handleStatusAction(campaign, 'complete')}
            className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
            title="Mark Complete"
          >
            <CheckCircle className="w-4 h-4" />
          </button>
        );
        break;
      case 'PAUSED':
        buttons.push(
          <button
            key="resume"
            onClick={() => handleStatusAction(campaign, 'resume')}
            className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50"
            title="Resume"
          >
            <Play className="w-4 h-4" />
          </button>
        );
        buttons.push(
          <button
            key="complete"
            onClick={() => handleStatusAction(campaign, 'complete')}
            className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
            title="Mark Complete"
          >
            <CheckCircle className="w-4 h-4" />
          </button>
        );
        break;
      case 'COMPLETED':
        buttons.push(
          <button
            key="archive"
            onClick={() => handleStatusAction(campaign, 'archive')}
            className="text-purple-600 hover:text-purple-800 p-1 rounded hover:bg-purple-50"
            title="Archive"
          >
            <Archive className="w-4 h-4" />
          </button>
        );
        break;
    }

    return buttons;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
            <Megaphone className="w-6 h-6 text-indigo-600" />
            <span>Campaign Management</span>
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Create and manage voice campaigns for outbound and inbound calls
          </p>
        </div>
        <button
          onClick={() => {
            setEditingCampaign(null);
            setShowModal(true);
          }}
          disabled={!selectedOrgId}
          className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          <Plus className="w-4 h-4" />
          <span>Create Campaign</span>
        </button>
      </div>

      {/* Notification */}
      {notification && (
        <div
          className={`flex items-center space-x-2 p-4 rounded-lg ${
            notification.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          {notification.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span className="text-sm font-medium">{notification.message}</span>
          <button
            onClick={() => setNotification(null)}
            className="ml-auto text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Organization Filter */}
      {showOrgFilter && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
          <OrganizationFilter
            value={selectedOrgId}
            onChange={(value) => {
              setSelectedOrgId(value);
            }}
            className="w-full md:w-96"
            requireSelection={true}
            allowServiceProviderSelection={true}
          />
        </div>
      )}

      {/* Show message if no org selected */}
      {showOrgFilter && !selectedOrgId && (
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <p className="text-blue-800 dark:text-blue-300 text-center">
            Please select a business from the dropdown above to view campaigns.
          </p>
        </div>
      )}

      {selectedOrgId && (
        <>
          {/* Filter Bar */}
          <div className="flex items-center justify-between gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search campaigns..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as CampaignStatus | '')}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">All Statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="ACTIVE">Active</option>
              <option value="PAUSED">Paused</option>
              <option value="COMPLETED">Completed</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </div>

          {/* Campaign Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Total Campaigns</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{campaigns.length}</p>
                </div>
                <Megaphone className="w-10 h-10 text-indigo-200" />
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Active</p>
                  <p className="text-2xl font-bold text-green-600">
                    {campaigns.filter(c => c.status === 'ACTIVE').length}
                  </p>
                </div>
                <Play className="w-10 h-10 text-green-200" />
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Paused</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {campaigns.filter(c => c.status === 'PAUSED').length}
                  </p>
                </div>
                <Pause className="w-10 h-10 text-yellow-200" />
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Completed</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {campaigns.filter(c => c.status === 'COMPLETED').length}
                  </p>
                </div>
                <CheckCircle className="w-10 h-10 text-blue-200" />
              </div>
            </div>
          </div>

          {/* Campaigns Table */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                <p className="mt-2 text-gray-500 dark:text-gray-400">Loading campaigns...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Campaign
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Progress
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Schedule
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredCampaigns.map(campaign => (
                      <tr key={campaign.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {campaign.campaignName}
                          </div>
                          {campaign.description && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">
                              {campaign.description}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                            {typeLabels[campaign.campaignType]}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              statusColors[campaign.status].bg
                            } ${statusColors[campaign.status].text}`}
                          >
                            {campaign.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm">
                            <div className="flex items-center space-x-2">
                              <div className="w-24 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                                <div
                                  className="bg-indigo-600 h-2 rounded-full"
                                  style={{
                                    width: `${
                                      campaign.totalScheduledCalls
                                        ? ((campaign.completedCalls || 0) / campaign.totalScheduledCalls) * 100
                                        : 0
                                    }%`,
                                  }}
                                ></div>
                              </div>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {campaign.completedCalls || 0}/{campaign.totalScheduledCalls || 0}
                              </span>
                            </div>
                            {campaign.successRate !== undefined && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {campaign.successRate.toFixed(1)}% success rate
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                          {campaign.startDate && (
                            <div className="flex items-center space-x-1">
                              <Calendar className="w-3 h-3" />
                              <span>{new Date(campaign.startDate).toLocaleDateString()}</span>
                            </div>
                          )}
                          {campaign.endDate && (
                            <div className="flex items-center space-x-1 text-xs">
                              <span>to {new Date(campaign.endDate).toLocaleDateString()}</span>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end space-x-1">
                            {getActionButtons(campaign)}
                            <button
                              onClick={() => handleViewStats(campaign)}
                              className="text-indigo-600 hover:text-indigo-800 p-1 rounded hover:bg-indigo-50"
                              title="View Statistics"
                            >
                              <BarChart3 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setEditingCampaign(campaign);
                                setShowModal(true);
                              }}
                              className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            {campaign.status === 'DRAFT' && (
                              <button
                                onClick={() => handleDeleteCampaign(campaign.id!, campaign.campaignName)}
                                className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredCampaigns.length === 0 && (
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    <Megaphone className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                    <p className="text-sm">
                      {searchTerm || statusFilter
                        ? 'No campaigns found matching your filters'
                        : 'No campaigns created yet'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Create/Edit Campaign Modal */}
      {showModal && (
        <CampaignModal
          campaign={editingCampaign}
          onSave={handleSaveCampaign}
          onClose={() => {
            setShowModal(false);
            setEditingCampaign(null);
          }}
        />
      )}

      {/* Campaign Stats Modal */}
      {showStatsModal && selectedCampaignStats && (
        <CampaignStatsModal
          campaign={selectedCampaignStats}
          onClose={() => {
            setShowStatsModal(false);
            setSelectedCampaignStats(null);
          }}
        />
      )}
    </div>
  );
};

// Campaign Modal Component
interface CampaignModalProps {
  campaign: CampaignDTO | null;
  onSave: (campaign: Partial<CampaignDTO>) => void;
  onClose: () => void;
}

const CampaignModal: React.FC<CampaignModalProps> = ({ campaign, onSave, onClose }) => {
  const [formData, setFormData] = useState<Partial<CampaignDTO>>(
    campaign || {
      campaignName: '',
      description: '',
      campaignType: 'OUTBOUND',
      maxConcurrentCalls: 10,
      callsPerMinute: 5,
      overrideBusinessHours: false,
      allowDuplicateRecipients: false,
    }
  );

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.campaignName?.trim()) {
      newErrors.campaignName = 'Campaign name is required';
    }

    if (!formData.campaignType) {
      newErrors.campaignType = 'Campaign type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
            <Megaphone className="w-5 h-5 text-indigo-600" />
            <span>{campaign ? 'Edit Campaign' : 'Create Campaign'}</span>
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Campaign Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Campaign Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="e.g., Holiday Sales 2024"
              value={formData.campaignName}
              onChange={e => setFormData({ ...formData, campaignName: e.target.value })}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                errors.campaignName ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            {errors.campaignName && (
              <p className="mt-1 text-xs text-red-600">{errors.campaignName}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
            <textarea
              placeholder="Campaign description..."
              value={formData.description || ''}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          {/* Campaign Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Campaign Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.campaignType}
              onChange={e => setFormData({ ...formData, campaignType: e.target.value as CampaignType })}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                errors.campaignType ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
              }`}
            >
              <option value="OUTBOUND">Outbound</option>
              <option value="INBOUND">Inbound</option>
              <option value="HYBRID">Hybrid</option>
            </select>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Start Date</label>
              <input
                type="date"
                value={formData.startDate?.slice(0, 10) || ''}
                onChange={e => setFormData({ ...formData, startDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">End Date</label>
              <input
                type="date"
                value={formData.endDate?.slice(0, 10) || ''}
                onChange={e => setFormData({ ...formData, endDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {/* Call Settings */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Max Concurrent Calls
              </label>
              <input
                type="number"
                min="1"
                max="100"
                value={formData.maxConcurrentCalls || 10}
                onChange={e => setFormData({ ...formData, maxConcurrentCalls: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Calls Per Minute
              </label>
              <input
                type="number"
                min="1"
                max="60"
                value={formData.callsPerMinute || 5}
                onChange={e => setFormData({ ...formData, callsPerMinute: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          {/* Override Business Hours */}
          <div className="pt-2">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.overrideBusinessHours || false}
                onChange={e => setFormData({ ...formData, overrideBusinessHours: e.target.checked })}
                className="mt-1 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              />
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">Override Business Hours</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Allow campaign to run outside configured business hours
                </div>
              </div>
            </label>

            <label className="flex items-start space-x-3 cursor-pointer mt-3">
              <input
                type="checkbox"
                checked={formData.allowDuplicateRecipients || false}
                onChange={e => setFormData({ ...formData, allowDuplicateRecipients: e.target.checked })}
                className="mt-1 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
              />
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-white">
                  Allow the same number more than once
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Lets a number already queued on this campaign be queued again, for retry
                  rounds and follow-ups. Off means a repeat is skipped, however it is spelled.
                </div>
              </div>
            </label>
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="tl-btn tl-btn-primary"
            >
              <Megaphone className="w-4 h-4" />
              <span>{campaign ? 'Update Campaign' : 'Create Campaign'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Campaign Stats Modal
interface CampaignStatsModalProps {
  campaign: CampaignDTO;
  onClose: () => void;
}

const CampaignStatsModal: React.FC<CampaignStatsModalProps> = ({ campaign, onClose }) => {
  const totalCalls = campaign.totalScheduledCalls || 0;
  const completedCalls = campaign.completedCalls || 0;
  const failedCalls = campaign.failedCalls || 0;
  const inProgressCalls = campaign.inProgressCalls || 0;
  const pendingCalls = totalCalls - completedCalls - failedCalls - inProgressCalls;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
            <BarChart3 className="w-5 h-5 text-indigo-600" />
            <span>Campaign Statistics</span>
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="px-6 py-4 space-y-4">
          <div className="text-center mb-4">
            <h4 className="text-xl font-bold text-gray-900 dark:text-white">{campaign.campaignName}</h4>
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                statusColors[campaign.status].bg
              } ${statusColors[campaign.status].text}`}
            >
              {campaign.status}
            </span>
          </div>

          {/* Progress */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Overall Progress</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {totalCalls > 0 ? ((completedCalls / totalCalls) * 100).toFixed(1) : 0}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3">
              <div
                className="bg-indigo-600 h-3 rounded-full"
                style={{
                  width: `${totalCalls > 0 ? (completedCalls / totalCalls) * 100 : 0}%`,
                }}
              ></div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 gap-4 pt-4">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalCalls}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Calls</p>
            </div>
            <div className="bg-green-50 dark:bg-green-900/30 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{completedCalls}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Completed</p>
            </div>
            <div className="bg-red-50 dark:bg-red-900/30 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-red-600">{failedCalls}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Failed</p>
            </div>
            <div className="bg-yellow-50 dark:bg-yellow-900/30 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-yellow-600">{inProgressCalls}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">In Progress</p>
            </div>
          </div>

          {/* Success Rate */}
          {campaign.successRate !== undefined && (
            <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-4 text-center mt-4">
              <p className="text-3xl font-bold text-blue-600">{campaign.successRate.toFixed(1)}%</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">Success Rate</p>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampaignManagement;
